import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
function AnimatedNumber({
  value,
  suffix,
  duration = 2
}: {
  value: number;
  suffix: string;
  duration?: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-40px'
  });
  const motionValue = useMotionValue(0);
  const rounded = useTransform(motionValue, v => {
    if (value < 10) return Number(v.toFixed(1));
    return Math.round(v);
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(motionValue, value, {
        duration,
        ease: [0.22, 1, 0.36, 1]
      });
      return controls.stop;
    }
  }, [isInView, value, duration, motionValue]);
  useEffect(() => {
    const unsubscribe = rounded.on('change', v => setDisplayValue(v));
    return unsubscribe;
  }, [rounded]);
  return <span ref={ref} data-id="element-1081">
      {displayValue.toLocaleString()}
      {suffix}
    </span>;
}
export function StatsDMRV() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const statVariant = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section ref={ref} className="w-full bg-carbon-deep py-20 sm:py-24" data-id="element-1082">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1083">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-1084">

          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1085">
            Built for Africa's climate economy
          </h2>
          <p className="mt-3 text-lg text-white/60" data-id="element-1086">
            Powering verified emissions data and trade compliance across East
            Africa's most critical sectors.
          </p>
        </motion.div>
        <motion.div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-1087">

          <motion.div className="text-center" variants={statVariant} data-id="element-1088">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-1089">
              <AnimatedNumber value={6} suffix="" duration={1.5} data-id="element-1090" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1091">
              dMRV Verticals
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1092">
              Coffee, cookstoves, forestry, agroforestry, energy & GHG
            </p>
          </motion.div>
          <motion.div className="text-center" variants={statVariant} data-id="element-1093">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-1094">
              <AnimatedNumber value={1.7} suffix="M+" duration={2} data-id="element-1095" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1096">
              Smallholder Farmers
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1097">
              Across Uganda's key agricultural regions
            </p>
          </motion.div>
          <motion.div className="text-center" variants={statVariant} data-id="element-1098">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-1099">
              $<AnimatedNumber value={2.4} suffix="B+" duration={2} data-id="element-1100" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1101">
              EU–Uganda Trade
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1102">
              Annual bilateral trade subject to EUDR & CBAM
            </p>
          </motion.div>
          <motion.div className="text-center" variants={statVariant} data-id="element-1103">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-1104">
              <AnimatedNumber value={100} suffix="%" duration={1.5} data-id="element-1105" />
            </p>
            <p className="mt-2 text-sm font-medium text-white/60 uppercase tracking-wider" data-id="element-1106">
              Audit-Grade Data
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-1107">
              Independent verification trusted by regulators & buyers
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}